import {GridFilterModel} from '@mui/x-data-grid-pro';
import {flatten} from '@wandb/weave/common/util/flatten';

function selectBestDisplayValue(stringEntries: [string, string][]): string {
  if (stringEntries.length === 0) {
    return '';
  }

  // Find entry with highest word-to-symbol ratio (most natural language-like)
  const withRatios = stringEntries
    .filter(([_, value]) => value.length > 3 && value.split(/\s+/).length >= 2)
    .map(([key, value]) => {
      const words = value.match(/\b\w+\b/g) || [];
      const symbols = value.match(/[^\w\s]/g) || [];
      const ratio = words.length / Math.max(symbols.length, 1);
      return {key, value, ratio};
    });

  if (withRatios.length > 0) {
    return withRatios.sort((a, b) => b.ratio - a.ratio)[0].value;
  }

  // Priority 2: Shorter text content that might be titles or labels
  const shortText = stringEntries.filter(([_, value]) => {
    return value.length > 3 && value.length <= 100 && /[a-zA-Z]/.test(value);
  });

  if (shortText.length > 0) {
    return shortText[0][1];
  }

  // Priority 3: Any string value
  return stringEntries[0][1];
}

export function extractChatMesage(value: any) {
  // if value is a string, return it
  if (typeof value === 'string') {
    return value;
  }

  // if value is an object, return the value of the 'content' key
  if (typeof value === 'object' && value !== null) {
    const flattened = flatten(value, {disregardSpecial: true});

    // filter out all keys that contains messages and content (the most typical OpenAI format)
    const filtered = Object.entries(flattened).filter(
      ([key, value]) =>
        key.includes('messages') &&
        key.includes('content') &&
        typeof value === 'string'
    );
    if (filtered.length > 0) {
      return filtered.at(-1)?.[1];
    } else {
      // filter all keys that have string value
      const filtered = Object.entries(flattened).filter(
        ([_, value]) => typeof value === 'string'
      );
      if (filtered.length > 0) {
        return selectBestDisplayValue(filtered);
      }
    }
  }

  // fallback to return the value
  return value;
}

// Convert GridFilterModel to backend datetime filters
// Only supports 'last_updated' field with date operators
export function convertFilterModelToDatetimeFilters(
  filterModel: GridFilterModel
): {after_datetime?: string; before_datetime?: string} {
  const filters: {after_datetime?: string; before_datetime?: string} = {};

  for (const item of filterModel.items) {
    if (item.field === 'started_at' && item.value) {
      if (item.operator === '(date): after') {
        filters.after_datetime = item.value;
      } else if (item.operator === '(date): before') {
        filters.before_datetime = item.value;
      }
    }
  }

  return filters;
}
